import type { Dayjs } from 'dayjs';

import type { DateRange, DateRanges, RangeAvailabilityDayMeta } from './types';

export const normalizeDateRanges = (ranges?: DateRanges): DateRange[] => {
  if (!ranges) {
    return [];
  }

  return Array.isArray(ranges) ? ranges : [ranges];
};

export const isDateWithinRange = (date: Dayjs, range?: DateRanges) => {
  const ranges = normalizeDateRanges(range);

  if (ranges.length === 0) {
    return false;
  }

  return ranges.some(
    (candidate) =>
      !date.isBefore(candidate.from, 'day') && !date.isAfter(candidate.to, 'day')
  );
};

export const isDateSelectable = (
  date: Dayjs,
  window: DateRange,
  excluded?: DateRanges
) => isDateWithinRange(date, window) && !isDateWithinRange(date, excluded);

export const normalizeSelectedDates = (dates: Dayjs[]) =>
  dates
    .map((date) => date.startOf('day'))
    .filter(
      (date, index, allDates) =>
        allDates.findIndex((candidate) => candidate.isSame(date, 'day')) === index
    )
    .sort((a, b) => a.valueOf() - b.valueOf());

export const toggleSelectedDate = (dates: Dayjs[], date: Dayjs) => {
  const normalizedDate = date.startOf('day');
  const isAlreadySelected = dates.some((selectedDate) => selectedDate.isSame(normalizedDate, 'day'));

  if (isAlreadySelected) {
    return dates.filter((selectedDate) => !selectedDate.isSame(normalizedDate, 'day'));
  }

  return normalizeSelectedDates([...dates, normalizedDate]);
};

export const getRangeAvailabilityDayMeta = (
  date: Dayjs,
  {
    window,
    preferred,
    excluded,
    selectedDates,
  }: {
    window: DateRange;
    preferred?: DateRanges;
    excluded?: DateRanges;
    selectedDates: Dayjs[];
  }
): RangeAvailabilityDayMeta => {
  const inWindow = isDateWithinRange(date, window);
  const inExcluded = isDateWithinRange(date, excluded);
  const inPreferred = isDateWithinRange(date, preferred);
  const isSelected = selectedDates.some((selectedDate) => selectedDate.isSame(date, 'day'));
  const isSelectable = isDateSelectable(date, window, excluded);

  if (isSelected) {
    return {
      variant: 'selected',
      isSelectable,
      showPreferredIndicator: inPreferred && !inExcluded,
    };
  }

  if (!inWindow) {
    return {
      variant: 'disabled',
      isSelectable: false,
      showPreferredIndicator: false,
    };
  }

  if (inExcluded) {
    return {
      variant: 'excluded',
      isSelectable: false,
      showPreferredIndicator: false,
    };
  }

  if (inPreferred) {
    return {
      variant: 'preferred',
      isSelectable,
      showPreferredIndicator: true,
    };
  }

  return {
    variant: 'window',
    isSelectable,
    showPreferredIndicator: false,
  };
};

export const getVisibleMonthRange = (visibleMonth: Dayjs, monthsToShow: number) => ({
  from: visibleMonth.startOf('month'),
  to: visibleMonth.add(monthsToShow - 1, 'month').endOf('month'),
});
